/**
 * O que este aparelho consegue fazer com Web Push, como função pura.
 *
 * ## A restrição que domina tudo
 *
 * `Docs/07-Integracoes/Web Push.md`: *"No iOS, Web Push só funciona com o PWA
 * instalado na tela de início (Safari 16.4+). Sem instalar, não existe push —
 * nenhum, de jeito nenhum."* E com a [[ADR-0002 - PWA como destino final]] não
 * há plano B nativo.
 *
 * Daí o estado `needsInstall` existir **antes** de qualquer pedido de permissão:
 * a mesma nota manda *"o app detecta se está em modo standalone e, se não
 * estiver, explica o que falta antes de sequer pedir permissão"*.
 *
 * ## Por que função pura
 *
 * Porque a decisão é uma tabela — seis entradas, um estado — e a alternativa é
 * um encadeado de `if` dentro de um componente, onde o caso do iOS não instalado
 * se confunde com "navegador velho". São coisas diferentes: uma tem conserto do
 * lado de quem usa, a outra não.
 *
 * **A ORDEM importa**, e é a parte que um teste precisa prender: `denied` vem
 * depois de `needsInstall`, porque no iOS sem instalar a permissão nem existe
 * para ser negada — o navegador reporta `'default'` e o pedido falha em
 * silêncio.
 */

export type PushState =
  /** Navegador sem service worker ou sem Push API. Não há o que oferecer. */
  | 'unsupported'
  /** iOS, e o app não está instalado na tela de início. É o passo que falta. */
  | 'needsInstall'
  /** A pessoa negou. Praticamente irreversível de dentro do app. */
  | 'denied'
  /** Dá para pedir. */
  | 'canAsk'
  /** Permitido, mas este aparelho ainda não está inscrito. */
  | 'notSubscribed'
  /** Tudo pronto. */
  | 'subscribed'

export type DeviceFacts = {
  hasServiceWorker: boolean
  hasPushManager: boolean
  /** iOS ou iPadOS. Só nele a instalação é pré-requisito. */
  isIOS: boolean
  /** Rodando como app instalado (`display-mode: standalone`). */
  isStandalone: boolean
  permission: 'default' | 'granted' | 'denied'
  /** Já existe uma `PushSubscription` para este aparelho. */
  isSubscribed: boolean
}

export function pushState(facts: DeviceFacts): PushState {
  // **`needsInstall` vem PRIMEIRO no iOS, inclusive antes de "sem suporte" — e
  // esta ordem é a correção de um erro meu que chegou ao aparelho do Murilo.**
  //
  // No Safari do iPhone, fora do app instalado, `PushManager` **não existe**.
  // A versão anterior checava suporte primeiro, concluía `unsupported`, e a tela
  // dizia *"este navegador não faz push"* — que é a única mensagem que encerra o
  // assunto. Ele viu exatamente isso, num iPhone perfeitamente capaz, com o app
  // a três toques de funcionar.
  //
  // A inversão de causa é o ponto: no iOS a ausência de suporte **é** a condição
  // de não-instalado, não uma limitação do aparelho. Instalar faz o `PushManager`
  // aparecer.
  //
  // E havia um caso de teste afirmando o comportamento errado — *"navegador sem
  // suporte ganha de tudo, nem instalar resolve"*. Ele codificava a minha
  // suposição, não o mundo.
  if (facts.isIOS && !facts.isStandalone) return 'needsInstall'

  if (!facts.hasServiceWorker || !facts.hasPushManager) return 'unsupported'

  if (facts.permission === 'denied') return 'denied'
  if (facts.permission === 'default') return 'canAsk'
  return facts.isSubscribed ? 'subscribed' : 'notSubscribed'
}
